import isEqual from 'lodash/isEqual'
import { Address, ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { TabPanel, TabView } from 'primereact/tabview'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { HealthEntityNoteDTO } from '../../dto/HealthEntityNoteDTO'
import { HealthEntityPlanDTO } from '../../dto/HealthEntityPlanDTO'
import { HealthEntitySaveDTO, defaultHealthEntitySaveDTO } from '../../dto/HealthEntitySaveDTO'
import useResizeListener from '../../hooks/useResizeListener'

import { Cuit, handleCUITValidation } from '../../utils/utils'
import HealthEntityAfipBillingTab from './tabs/HealthEntityAfipBillingTab'
import HealthEntityFiscalDataTab from './tabs/HealthEntityFiscalDataTab'
import HealthEntityGeneralTab from './tabs/HealthEntityGeneralTab'
import HealthEntityNotesTab from './tabs/HealthEntityNotesTab'
import HealthEntityOtherDataTab from './tabs/HealthEntityOtherDataTab'
import HealthEntityPlansTab from './tabs/HealthEntityPlansTab'
import MobileTabSelector, { MobileTabSelectorOption } from '../Location/components/MobileTabSelector'
import { useHealthEntityService } from '../../servicesHooks/HealthEntity/useHealthEntityService'

interface HealthEntityCrudPanelProps {
  healthEntityId: number
  setHealthEntityId: (healthEntityId: number | null) => void
  mode?: 'edit' | 'view'
  setMode?: (mode: 'edit' | 'view') => void
  onSaved?: () => void
}

type UnknownRecord = Record<string, unknown>
type GeneralValidationField = 'commercialname'

export interface HealthEntityGeneralValidationErrors {
  commercialname?: string
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function isFiscalAddress(address: Address): boolean {
  const types = address?.types ?? []
  return types.some((type) => {
    const id = Number(type?.id ?? -1)
    const description = String(type?.description ?? '').toLowerCase()
    return id === 3 || description.includes('fiscal')
  })
}

function normalizeFiscalAddressReference(healthEntity: HealthEntitySaveDTO): Pick<HealthEntitySaveDTO, 'fiscaladdress' | 'fiscaladdressdesc'> {
  const addresses = healthEntity.addresses ?? []
  const selectedFiscalAddressId = Number(healthEntity.fiscaladdress ?? -1)

  if (!Number.isFinite(selectedFiscalAddressId) || selectedFiscalAddressId <= 0) {
    return {
      fiscaladdress: null,
      fiscaladdressdesc: '',
    }
  }

  const selectedAddress = addresses.find(address => Number(address?.address ?? -1) === selectedFiscalAddressId)
  if (!selectedAddress || !isFiscalAddress(selectedAddress)) {
    return {
      fiscaladdress: null,
      fiscaladdressdesc: '',
    }
  }

  return {
    fiscaladdress: selectedFiscalAddressId,
    fiscaladdressdesc: String(selectedAddress.addressdesc ?? '').trim(),
  }
}

export default function HealthEntityCrudPanel({
  healthEntityId,
  setHealthEntityId,
  mode = 'edit',
  setMode,
  onSaved,
}: Readonly<HealthEntityCrudPanelProps>) {
  const dispatch = useDispatch()
  const { getHealthEntityById, saveHealthEntity, updateHealthEntity } = useHealthEntityService()
  const isMobile = useResizeListener(768)
  const [healthEntity, setHealthEntity] = useState<HealthEntitySaveDTO>({ ...defaultHealthEntitySaveDTO })
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [generalValidationErrors, setGeneralValidationErrors] = useState<HealthEntityGeneralValidationErrors>({})
  const [initialHealthEntitySnapshot, setInitialHealthEntitySnapshot] = useState<HealthEntitySaveDTO>({ ...defaultHealthEntitySaveDTO })
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  const isEditing = healthEntityId > 0
  const isReadOnly = isEditing && mode === 'view'
  const showAfipBillingTab = isEditing && !!healthEntity.isThisInstitution
  const hasUnsavedChanges = useMemo(() => {
    if (isReadOnly || loading) return false
    return !isEqual(healthEntity, initialHealthEntitySnapshot)
  }, [healthEntity, initialHealthEntitySnapshot, isReadOnly, loading])

  useEffect(() => {
    setActiveIndex(0)
    setGeneralValidationErrors({})
    setShowUnsavedChangesModal(false)
  }, [healthEntityId, mode])

  useEffect(() => {
    if (!isEditing) {
      const initialState = { ...defaultHealthEntitySaveDTO }
      setHealthEntity(initialState)
      setInitialHealthEntitySnapshot(initialState)
      setGeneralValidationErrors({})
      return
    }

    setLoading(true)
    getHealthEntityById(healthEntityId).then((resp) => {
      const loadedEntity: HealthEntitySaveDTO = {
        ...defaultHealthEntitySaveDTO,
        ...resp,
        classification: resp?.classification ?? defaultHealthEntitySaveDTO.classification,
        classificationdesc: resp?.classificationdesc || defaultHealthEntitySaveDTO.classificationdesc,
        plans: resp?.plans ?? [],
        notes: resp?.notes ?? [],
        addresses: resp?.addresses ?? defaultHealthEntitySaveDTO.addresses,
      }
      setHealthEntity(loadedEntity)
      setInitialHealthEntitySnapshot(loadedEntity)
      setGeneralValidationErrors({})
    }).finally(() => setLoading(false))
  }, [healthEntityId, isEditing])

  const tabItems = useMemo(() => {
    const items = [
      { label: 'Datos Generales' },
      { label: 'Planes', disabled: !isEditing },
      { label: 'Recetas y turnos', disabled: !isEditing },
      { label: 'Facturación', disabled: !isEditing },
      { label: 'Notas', disabled: !isEditing },
    ]
    if (showAfipBillingTab) {
      items.splice(4, 0, { label: 'Vinculación con ARCA', disabled: !isEditing })
    }
    return items
  }, [isEditing, showAfipBillingTab])

  const mobileTabOptions = useMemo<MobileTabSelectorOption[]>(() => {
    return tabItems.map((item, index) => ({
      id: index,
      label: item.label,
      disabled: Boolean(item.disabled),
    }))
  }, [tabItems])

  function resolveSaveErrorMessage(error: unknown) {
    if (isRecord(error)) {
      const response = isRecord(error.response) ? error.response : null
      const responseData = response ? response.data : null
      if (typeof responseData === 'string' && responseData.trim() !== '') return responseData
      if (isRecord(responseData) && typeof responseData.message === 'string' && responseData.message.trim() !== '') {
        return responseData.message
      }
      if (isRecord(responseData) && typeof responseData.error === 'string' && responseData.error.trim() !== '') {
        return responseData.error
      }
      if (typeof error.message === 'string' && error.message.trim() !== '') {
        return error.message
      }
    }
    return 'No se pudieron guardar los cambios'
  }

  function getGeneralValidationErrors(): HealthEntityGeneralValidationErrors {
    const errors: HealthEntityGeneralValidationErrors = {}

    if (healthEntity.commercialname.trim() === '') {
      errors.commercialname = 'El nombre comercial es obligatorio'
    }

    return errors
  }

  function clearGeneralValidationError(field: GeneralValidationField) {
    setGeneralValidationErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  function validateForm() {
    const nextGeneralValidationErrors = getGeneralValidationErrors()
    const hasGeneralErrors = Object.keys(nextGeneralValidationErrors).length > 0
    if (hasGeneralErrors) {
      return {
        message: nextGeneralValidationErrors.commercialname ?? 'Completar datos obligatorios',
        generalErrors: nextGeneralValidationErrors,
      }
    }

    if ((healthEntity.classification ?? -1) <= 0) {
      return { message: 'La clasificación es obligatoria', generalErrors: {} }
    }
    if (healthEntity.cuit.trim() !== '' && !handleCUITValidation(healthEntity.cuit.trim() as Cuit)) {
      return { message: 'El CUIT ingresado no es válido', generalErrors: {} }
    }
    if (healthEntity.plans.some(plan => (plan.name ?? '').trim() === '')) {
      return { message: 'Todos los planes deben tener nombre', generalErrors: {} }
    }
    const normalizedPlanNames = healthEntity.plans.map(plan => (plan.name ?? '').trim().toLowerCase()).filter(name => name !== '')
    if (new Set(normalizedPlanNames).size !== normalizedPlanNames.length) {
      return { message: 'Hay planes con nombre duplicado', generalErrors: {} }
    }
    if (healthEntity.notes.some(note => note.note.trim() === '')) {
      return { message: 'Las notas no pueden estar vacías', generalErrors: {} }
    }
    return { message: '', generalErrors: {} }
  }

  function closeCrudPanel() {
    setShowUnsavedChangesModal(false)
    setHealthEntityId(null)
  }

  function onAttemptCloseCrudPanel() {
    if (isReadOnly || !hasUnsavedChanges) {
      closeCrudPanel()
      return
    }
    setShowUnsavedChangesModal(true)
  }

  async function save(options: { exitAfterSave?: boolean } = {}): Promise<boolean> {
    if (isReadOnly) return false

    if (options.exitAfterSave) {
      setShowUnsavedChangesModal(false)
    }

    const validation = validateForm()
    const validationMessage = validation.message
    setGeneralValidationErrors(validation.generalErrors)
    if (validationMessage !== '') {
      if (Object.keys(validation.generalErrors).length > 0) {
        setActiveIndex(0)
      }
      dispatch(setNotification({
        title: 'Validación',
        desc: validationMessage,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-exclamation-triangle',
      }))
      return false
    }

    const normalizedFiscalAddress = normalizeFiscalAddressReference(healthEntity)

    const payload: HealthEntitySaveDTO = {
      ...healthEntity,
      businessname: healthEntity.businessname.trim() !== ''
        ? healthEntity.businessname.trim()
        : healthEntity.commercialname.trim(),
      classification: (healthEntity.classification ?? -1) > 0
        ? healthEntity.classification
        : defaultHealthEntitySaveDTO.classification,
      classificationdesc: healthEntity.classificationdesc.trim() !== ''
        ? healthEntity.classificationdesc.trim()
        : defaultHealthEntitySaveDTO.classificationdesc,
      addresses: healthEntity.addresses ?? [],
      plans: healthEntity.plans.map(plan => ({
        ...plan,
        name: (plan.name ?? '').trim(),
        description: (plan.description ?? '').trim(),
      })),
      notes: healthEntity.notes.map(note => ({
        ...note,
        note: note.note.trim(),
      })),
      fiscaladdress: normalizedFiscalAddress.fiscaladdress,
      fiscaladdressdesc: normalizedFiscalAddress.fiscaladdressdesc,
    }

    setLoading(true)
    try {
      const createdId = isEditing
        ? await updateHealthEntity(payload).then(() => null)
        : await saveHealthEntity(payload).then((savedId) => savedId)

      dispatch(setNotification({
        title: isEditing ? 'Obra Social actualizada' : 'Obra Social creada',
        desc: 'Los cambios fueron guardados correctamente',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))

      if (onSaved) onSaved()

      if (options.exitAfterSave) {
        closeCrudPanel()
        return true
      }

      if (!isEditing && createdId != null && createdId > 0) {
        setMode?.('edit')
        setHealthEntityId(createdId)
        return true
      }

      setHealthEntityId(null)
      return true
    } catch (error) {
      const message = resolveSaveErrorMessage(error)
      console.error('HealthEntity save failed', error)
      dispatch(setNotification({
        title: 'Error',
        desc: message,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      return false
    } finally {
      setLoading(false)
    }
  }

  function renderTabContent(tabIndex: number) {
    const currentTabLabel = tabItems[tabIndex]?.label

    switch (currentTabLabel) {
      case 'Datos Generales':
        return (
          <HealthEntityGeneralTab
            healthEntity={healthEntity}
            setHealthEntity={setHealthEntity}
            validationErrors={generalValidationErrors}
            isMobile={isMobile}
            onClearValidationError={clearGeneralValidationError}
            readOnly={isReadOnly}
          />
        )
      case 'Planes':
        return (
          <HealthEntityPlansTab
            plans={healthEntity.plans}
            isMobile={isMobile}
            readOnly={isReadOnly}
            setPlans={(plansUpdater: React.SetStateAction<HealthEntityPlanDTO[]>) => {
              setHealthEntity(prev => ({
                ...prev,
                plans: typeof plansUpdater === 'function' ? plansUpdater(prev.plans) : plansUpdater,
              }))
            }}
          />
        )
      case 'Recetas y turnos':
        return <HealthEntityOtherDataTab healthEntity={healthEntity} setHealthEntity={setHealthEntity} readOnly={isReadOnly} />
      case 'Facturación':
        return <HealthEntityFiscalDataTab healthEntity={healthEntity} setHealthEntity={setHealthEntity} readOnly={isReadOnly} />
      case 'Vinculación con ARCA':
        return (
          <HealthEntityAfipBillingTab
            healthEntity={healthEntity}
            setHealthEntity={setHealthEntity}
            healthEntityId={healthEntity.medereentity ?? null}
            readOnly={isReadOnly}
          />
        )
      case 'Notas':
      default:
        return (
          <HealthEntityNotesTab
            notes={healthEntity.notes}
            isMobile={isMobile}
            readOnly={isReadOnly}
            setNotes={(notesUpdater: React.SetStateAction<HealthEntityNoteDTO[]>) => {
              setHealthEntity(prev => ({
                ...prev,
                notes: typeof notesUpdater === 'function' ? notesUpdater(prev.notes) : notesUpdater,
              }))
            }}
          />
        )
    }
  }

  return (
    <div className='viewContainer health-entity-view'>
      <div className='health-entity-back-action'>
        <Button className='btnCancel' label='<< Volver' onClick={onAttemptCloseCrudPanel} />
      </div>
      <div className='health-entity-main-header'>
        <h1 className='health-entity-title health-entity-main-title'>Obras Sociales</h1>
        {isReadOnly && (
          <Button
            className='buttonMain3 health-entity-view-edit-btn health-entity-primary-btn'
            icon='pi pi-file-edit'
            label='Editar'
            onClick={() => setMode?.('edit')}
          />
        )}
      </div>
      {isEditing && (
        <div className='health-entity-subtitle-row'>
          <p className='health-entity-subtitle'>
            {healthEntity.commercialname?.trim() || healthEntity.businessname?.trim() || `ID ${healthEntityId}`}
          </p>
        </div>
      )}
      <div className='health-entity-heading-divider' />
      {isMobile ? (
        <MobileTabSelector
          id='healthentitytabselectorid'
          className='health-entity-mobile-tab-selector mb-3'
          value={activeIndex}
          options={mobileTabOptions}
          onChange={(id) => setActiveIndex(Number(id))}
          ariaLabel='Seleccionar tab de obras sociales'
        />
      ) : null}

      <div className={`health-entity-crud-content ${isReadOnly ? 'health-entity-crud-content-readonly' : ''}`}>
        {isMobile ? (
          renderTabContent(activeIndex)
        ) : (
          <TabView
            className='health-entity-tabview'
            activeIndex={activeIndex}
            onTabChange={(e) => {
              if (tabItems[e.index]?.disabled) return
              setActiveIndex(e.index)
            }}
          >
            {tabItems.map((item, index) => (
              <TabPanel
                key={`health-entity-tab-${item.label}`}
                header={item.label}
                disabled={Boolean(item.disabled)}
              >
                {renderTabContent(index)}
              </TabPanel>
            ))}
          </TabView>
        )}
      </div>

      <div className='health-entity-crud-actions'>
        <Button
          className={`buttonMain2 health-entity-action-btn ${!isReadOnly ? 'health-entity-cancel-btn' : ''}`.trim()}
          label={isReadOnly ? 'Cerrar' : 'Cancelar'}
          onClick={onAttemptCloseCrudPanel}
        />
        {!isReadOnly && (
          <Button
            className='buttonMain3 health-entity-action-btn health-entity-primary-btn'
            label={loading ? 'Guardando...' : (isEditing ? 'Guardar' : 'Crear')}
            onClick={() => { void save() }}
            disabled={loading}
          />
        )}
      </div>

      <Modal
        className='health-entity-unsaved-dialog'
        visible={showUnsavedChangesModal}
        setVisible={setShowUnsavedChangesModal}
        width='w-medium'
        header={<p className='info-dark text-3xl health-entity-unsaved-modal-title'>Estás por cerrar el formulario.</p>}
        footerButtonRightText={loading ? 'Guardando...' : 'Guardar y salir'}
        onClickRightBtn={() => { void save({ exitAfterSave: true }) }}
        rightButtonDisabled={loading}
        extraButtons={(
          <>
            <Button
              style={{ order: -5 }}
              className='buttonMain3'
              label='Cancelar'
              disabled={loading}
              onClick={() => setShowUnsavedChangesModal(false)}
            />
            <Button
              style={{ order: 0 }}
              className='buttonMain2'
              label='Salir sin guardar'
              disabled={loading}
              onClick={closeCrudPanel}
            />
          </>
        )}
      >
        <div className='health-entity-unsaved-modal'>
          <p className='health-entity-unsaved-subtitle'>Hay cambios sin guardar</p>
        </div>
      </Modal>
    </div>
  )
}
